import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';

export interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    variant?: 'default' | 'workspace' | 'confirmation' | 'form';
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
    backdrop?: 'blur' | 'dark' | 'light';
    closeOnBackdrop?: boolean;
    closeOnEscape?: boolean;
    className?: string;
}

export interface ModalHeaderProps {
children?: React.ReactNode;
icon?: React.ReactNode;
title?: string;
subtitle?: string;
onClose?: () => void;
showCloseButton?: boolean;
className?: string;
}

export interface ModalContentProps {
    children: React.ReactNode;
    className?: string;
    padding?: boolean;
}

export interface ModalFooterProps {
    children: React.ReactNode;
    className?: string;
    justify?: 'start' | 'center' | 'end' | 'between';
}

// Base Modal Component
export const Modal: React.FC<ModalProps> = ({
                                                isOpen,
                                                onClose,
                                                children,
                                                variant = 'default',
                                                size = 'md',
                                                backdrop = 'blur',
                                                closeOnBackdrop = true,
                                                closeOnEscape = true,
                                                className = ''
                                            }) => {
    // Handle escape key
    useEffect(() => {
        if (!isOpen || !closeOnEscape) return;

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, closeOnEscape, onClose]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = 'unset';

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    // Size classes
    const getSizeClasses = () => {
        switch (size) {
            case 'sm': return 'w-full max-w-md';
            case 'md': return 'w-full max-w-lg';
            case 'lg': return 'w-full max-w-2xl';
            case 'xl': return 'w-full max-w-4xl';
            case 'full': return 'w-full h-full max-w-none max-h-none m-0';
            default: return 'w-full max-w-lg';
        }
    };

    // Backdrop classes
    const getBackdropClasses = () => {
        switch (backdrop) {
            case 'blur': return 'bg-black/50 backdrop-blur-sm';
            case 'dark': return 'bg-black/60';
            case 'light': return 'bg-black/30';
            default: return 'bg-black/50 backdrop-blur-sm';
        }
    };

    // Variant-specific classes
    const getVariantClasses = () => {
        switch (variant) {
            case 'workspace': return 'max-h-[80vh] flex flex-col';
            case 'confirmation': return 'max-h-[60vh]';
            case 'form': return 'max-h-[70vh]';
            default: return 'max-h-[90vh]';
        }
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose();
    };

    const modalContent = (
        <div
            className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${getBackdropClasses()}`}
            onClick={handleBackdropClick}
        >
            <div
                className={`
          bg-white rounded-lg shadow-xl transition-all duration-200 ease-out
          ${getSizeClasses()}
          ${getVariantClasses()}
          ${className}
        `}
                onClick={(e) => e.stopPropagation()}
            >
                {children}
            </div>
        </div>
    );

    return ReactDOM.createPortal(modalContent, document.body);
};

// Modal Header Component
export const ModalHeader: React.FC<ModalHeaderProps> = ({
                                                            children,
                                                            icon,
                                                            title,
                                                            subtitle,
                                                            onClose,
                                                            showCloseButton = true,
                                                            className = ''
                                                        }) => {
    return (
        <div className={`px-6 py-4 border-b border-gray-200 ${className}`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 flex-1">
                    {icon && <div className="flex-shrink-0">{icon}</div>}
                    <div className="flex-1 min-w-0">
                        {title && <h2 className="text-xl font-semibold text-gray-900 truncate">{title}</h2>}
                        {subtitle && <p className="mt-1 text-sm text-gray-600">{subtitle}</p>}
                        {!title && !subtitle && children}
                    </div>
                </div>

                {showCloseButton && onClose && (
                    <button
                        onClick={onClose}
                        className="ml-3 flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Close modal"
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                )}
            </div>

            {(title || subtitle) && children && <div className="mt-3">{children}</div>}
        </div>
    );
};

// Modal Content Component
export const ModalContent: React.FC<ModalContentProps> = ({
                                                              children,
                                                              className = '',
                                                              padding = true
                                                          }) => {
    return (
        <div className={`flex-1 overflow-hidden ${padding ? 'px-6 py-4' : ''} ${className}`}>
            {children}
        </div>
    );
};

// Modal Footer Component
export const ModalFooter: React.FC<ModalFooterProps> = ({
                                                            children,
                                                            className = '',
                                                            justify = 'end'
                                                        }) => {
    const getJustifyClasses = () => {
        switch (justify) {
            case 'start': return 'justify-start';
            case 'center': return 'justify-center';
            case 'end': return 'justify-end';
            case 'between': return 'justify-between';
            default: return 'justify-end';
        }
    };

    return (
        <div className={`px-6 py-4 border-t border-gray-200 bg-gray-50 ${className}`}>
            <div className={`flex items-center space-x-3 ${getJustifyClasses()}`}>
                {children}
            </div>
        </div>
    );
};

export default Modal;
